import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { forkJoin, noop, Observable, of, tap, zip } from 'rxjs';
import { Account } from 'app/core/models/account.types';
import { AccountsService } from 'app/modules/accounts/accounts.service';
import { User } from 'app/core/models/user.types';
import { AccountOnboardingComponent } from 'app/modules/accounts/modals/account-onboarding/account-onboarding.component';
import { environment } from '@env/environment';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AccountService } from 'app/core';
import { ReservedRole } from 'app/core/enums';
import { Store } from '@ngxs/store';
import { UserState } from 'app/state/user/state';
import { Role } from 'app/core/models';
import { getSubdomain } from 'app/shared/Utils/common.utils';

type AccountWithCollaboratorFlag = Account & { isCollaborator: boolean };

@Component({
  selector: 'accounts-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountsListComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  private _accounts: AccountWithCollaboratorFlag[];
  accounts$: Observable<Account[]>;
  accountsCount: number;
  appDomain: string;
  user: User;

  get accounts(): Account[] {
    return this._accounts;
  }

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _changeDetectorRef: ChangeDetectorRef,
    private _accountsService: AccountsService,
    private _matDialog: MatDialog,
    private _router: Router,
    private readonly accountService: AccountService,
    private readonly store: Store,
  ) {
    super();
    this.accountsCount = 0;
  }

  ngOnInit(): void {
    this.appDomain = environment.appDomain;
    this.user = this.store.selectSnapshot(UserState.getUser);
    this._accounts = this._accountsService.accountsValue as AccountWithCollaboratorFlag[];

    if (this._accounts?.length) {
      this.accountsCount = this._accounts.length;

      if (this.accountsCount === 1) {
        const [account] = this._accounts;

        if (getSubdomain() && this.user?.roles) {
          this.handleRedirect(account, this.user.roles);
        } else {
          this.accountService
            .getUserAccountRoles(+account.id)
            .pipe(
              untilComponentDestroyed(this),
              tap((userRoles) => this.handleRedirect(account, userRoles)),
            )
            .subscribe(noop);
        }
      } else {
        this.handleAccountRoles();
      }
    }
  }

  showOnboardingModal(): void {
    this._matDialog
      .open(AccountOnboardingComponent, {
        panelClass: 'mailbox-compose-dialog',
        width: '50vw',
        disableClose: true,
        data: {},
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe();
  }

  private isCollaborator(roles: Role[]): boolean {
    return roles.some((role) => role.name === ReservedRole.Collaborator);
  }

  private handleRedirect(account: Account, roles: Role[]): void {
    if (this.isCollaborator(roles)) {
      window.location.assign(`https://${account.subdomain}.${this.appDomain}/tasks/global`);
    } else {
      window.location.assign(`https://${account.subdomain}.${this.appDomain}/projects`);
    }
  }

  private handleAccountRoles(): void {
    forkJoin(this._accounts.map(({ id }) => zip([of(+id), this.accountService.getUserAccountRoles(+id)])))
      .pipe(
        untilComponentDestroyed(this),
        tap((x) => {
          this._accounts = this._accounts.map((y) => {
            const [_, userRoles] = x.find(([accountId, _]) => accountId === +y.id);
            return { ...y, isCollaborator: this.isCollaborator(userRoles) };
          });

          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);
  }
}
